import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

/**
 * M10 L10.08 — Painter Performance Update (§17).
 *
 * Records painter performance deltas for the review via
 * `POST /v1/quality/reviews/:reviewId/performance`, returning a pending update
 * that the reviewer can then apply via `POST /v1/quality/performance/:id/apply`.
 */
interface PerfUpdate {
  readonly perfUpdateId: string;
  readonly applyStatus?: string;
}

const DELTA_FIELDS = [
  ['completionRateDelta', 'Completion rate Δ'],
  ['qualityScoreDelta', 'Quality score Δ'],
  ['reworkCountDelta', 'Rework count Δ'],
  ['reviewScoreDelta', 'Review score Δ'],
  ['delayPctDelta', 'Delay % Δ'],
  ['compliancePctDelta', 'Compliance % Δ'],
] as const;

type DeltaKey = (typeof DELTA_FIELDS)[number][0];

export function QualityPerformancePage(): ReactNode {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [deltas, setDeltas] = useState<Record<DeltaKey, string>>(() =>
    Object.fromEntries(DELTA_FIELDS.map(([k]) => [k, ''])) as Record<DeltaKey, string>,
  );
  const [pending, setPending] = useState<PerfUpdate | null>(null);
  const [applied, setApplied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onRecord = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    setApplied(false);
    try {
      const body: Record<string, number> = {};
      for (const [k] of DELTA_FIELDS) {
        if (deltas[k].trim() !== '') body[k] = Number(deltas[k]);
      }
      const res = await apiRequest<PerfUpdate>(`/v1/quality/reviews/${reviewId}/performance`, {
        method: 'POST', token, body,
      });
      setPending(res);
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, deltas]);

  const onApply = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/quality/performance/${pending.perfUpdateId}/apply`, { method: 'POST', token });
      setApplied(true);
    } catch (err) { setActionError((err as Error).message); }
    finally { setBusy(false); }
  }, [pending, token]);

  return (
    <main aria-label="quality-performance">
      <p style={{ marginBottom: 8 }}><Link to={`/quality/review/${reviewId}`}>{t('quality.common.back')}</Link></p>
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('quality.performance.title')}</h1>

      <form onSubmit={onRecord} aria-label="performance-form" style={{ maxWidth: 480, marginBottom: 20 }}>
        {DELTA_FIELDS.map(([k]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <label htmlFor={`perf-${k}`} style={{ width: 160, fontSize: 13 }}>{t(`quality.performance.delta.${k}`)}</label>
            <input id={`perf-${k}`} data-testid={`perf-${k}`} type="number" step="any" value={deltas[k]}
                   onChange={(e) => setDeltas((d) => ({ ...d, [k]: e.target.value }))}
                   style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)', width: 120 }} />
          </div>
        ))}
        <Button type="submit" data-testid="perf-record" disabled={busy} variant="primary" style={{ marginTop: 8 }}>
          {busy ? t('quality.performance.recording') : t('quality.performance.record')}
        </Button>
      </form>

      {pending && (
        <section aria-label="pending-perf" style={{ borderTop: '1px solid var(--color-border-default)', paddingTop: 12 }}>
          <p data-testid="perf-pending" style={{ fontSize: 13 }}>
            {t('quality.performance.recorded', { status: applied ? 'APPLIED' : (pending.applyStatus ?? 'PENDING') })}
          </p>
          {!applied && (
            <Button type="button" data-testid="perf-apply" disabled={busy} onClick={() => void onApply()} variant="primary">
              {t('quality.performance.apply')}
            </Button>
          )}
          {applied && <p data-testid="perf-applied" style={{ color: 'var(--color-success)', fontSize: 13 }}>{t('quality.performance.applied')}</p>}
        </section>
      )}
      {actionError && <p role="alert" style={{ color: 'var(--color-danger)', marginTop: 12 }}>{actionError}</p>}
    </main>
  );
}
